import { clientExperiments, clientExperimentStoragePrefix } from '@/constants'
import Analytics from '@/Analytics'
import util from '@/util'

/**
 * Get localStorage key for a client experiment
 * @param {string} experimentKey
 * @returns {string}
 */
export function getExperimentStorageKey(experimentKey) {
	return `${clientExperimentStoragePrefix}${experimentKey}`
}

/**
 * Get a stable user identifier for experiment bucketing
 * Prefers logged-in user UUID, falls back to anonymous ID
 * @returns {string}
 */
export function getExperimentUserId() {
	// Prefer logged-in user UUID
	const userInfo = localStorage.getObject('userInfo')
	if (userInfo?.user_uuid) return userInfo.user_uuid

	// Fall back to anonymous ID (generate if needed)
	const storageKey = 'experiment:userId'
	let anonId = localStorage.getItem(storageKey)
	if (!anonId) {
		anonId = util.uuidv4()
		try {
			localStorage.setItem(storageKey, anonId)
		} catch (e) {
			// Storage may fail in private mode or quota exceeded
		}
	}
	return anonId
}

/**
 * Hash a string to a positive 32-bit integer
 * Uses full hash range to minimize modulo bias (~0.0000001% for typical variant counts)
 * @param {string} str
 * @returns {number}
 */
export function hashToNumber(str) {
	let hash = 0
	for (let i = 0; i < str.length; i++) {
		hash = (hash << 5) - hash + str.charCodeAt(i)
		hash |= 0 // Force 32-bit signed integer
	}
	return Math.abs(hash)
}

/**
 * Hash a string to a number 0-99 for enrollment bucketing
 * @param {string} str
 * @returns {number}
 */
export function hashToPercent(str) {
	return hashToNumber(str) % 100
}

/**
 * Check if user is enrolled in experiment based on rollout percentage
 * @param {string} experimentKey
 * @param {number} rollout - Enrollment percentage (0-1)
 * @param {string} userId
 * @returns {boolean}
 */
export function isEnrolled(experimentKey, rollout, userId) {
	return hashToPercent(userId + experimentKey) < rollout * 100
}

/**
 * Pick a variant deterministically for a given user
 * Note: Key format differs from enrollment to ensure independence between
 * enrollment bucket and variant assignment (prevents correlation)
 * @param {string} experimentKey
 * @param {object} experiment
 * @param {string} userId
 * @returns {string}
 */
export function pickVariant(experimentKey, experiment, userId) {
	const hash = hashToNumber(`${experimentKey}:variant:${userId}`)
	return experiment.variants[hash % experiment.variants.length]
}

/**
 * Pure decision: given all inputs, what variant (if any) applies?
 * Returns the variant plus whether this is a fresh assignment (so the caller
 * knows whether to persist, sync, and fire analytics).
 * @param {object} args
 * @param {string} args.experimentKey
 * @param {object} args.experiment
 * @param {string} args.userId
 * @param {string|null} args.storedVariant
 * @param {boolean} args.experimentsDisabled
 * @returns {{variant: string|null, assigned: boolean}}
 */
export function resolveVariant({ experimentKey, experiment, userId, storedVariant, experimentsDisabled }) {
	if (experimentsDisabled) {
		return { variant: null, assigned: false }
	}
	if (storedVariant && experiment.variants.includes(storedVariant)) {
		return { variant: storedVariant, assigned: false }
	}
	if (!isEnrolled(experimentKey, experiment.rollout, userId)) {
		return { variant: null, assigned: false }
	}
	return { variant: pickVariant(experimentKey, experiment, userId), assigned: true }
}

/**
 * Sync all client experiment assignments to PostHog person properties
 */
export function syncClientExperimentsToPostHog() {
	try {
		const assignments = {}
		for (const key of Object.keys(clientExperiments)) {
			const variant = localStorage.getItem(getExperimentStorageKey(key))
			if (variant) assignments[key] = variant
		}
		Analytics.setClientExperiments(assignments)
	} catch (e) {
		// localStorage may be unavailable - sync will retry on next assignment
	}
}

/**
 * Get the active variant for a client experiment
 * @param {string} experimentKey - Key from clientExperiments config
 * @returns {string|null} - Variant name or null if not enrolled/doesn't exist
 */
export function getClientExperimentVariant(experimentKey) {
	const experiment = clientExperiments[experimentKey]
	if (!experiment) {
		if (process.env.NODE_ENV !== 'production') {
			console.warn(`[experiments] Unknown client experiment: ${experimentKey}`)
		}
		return null
	}

	const storageKey = getExperimentStorageKey(experimentKey)
	const { variant, assigned } = resolveVariant({
		experimentKey,
		experiment,
		userId: getExperimentUserId(),
		storedVariant: localStorage.getItem(storageKey),
		experimentsDisabled: localStorage.getItem('experiments:disabled') === 'true',
	})

	if (assigned) {
		try {
			localStorage.setItem(storageKey, variant)
		} catch (e) {
			// Storage may fail in private mode or quota exceeded - continue without persistence
		}
		syncClientExperimentsToPostHog()
		Analytics.capture('experiment:assigned', {
			experiment: experimentKey,
			variant,
		})
	}

	return variant
}
